import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./Global.css";
import "./Admin/DesktopMainLayout.css";

// Public Pages
const Home = lazy(() => import("./Home"));
const About = lazy(() => import("./About"));
const Tools = lazy(() => import("./Tools"));
const Contact = lazy(() => import("./Contact"));
const ExpenseTracker = lazy(() => import("./ExpenseTracker"));

// Auth Pages
const Login = lazy(() => import("./Login"));
const Signup = lazy(() => import("./Signup"));

// Shared / Role-Independent Pages (Admin & User)
const Dashboard = lazy(() => import("./Admin/Dashboard"));
const AdminProfile = lazy(() => import("./Admin/AdminProfile"));
const AdmEditProfile = lazy(() => import("./Admin/AdmEditProfile"));
const AdminMediaLibrary = lazy(() => import("./Admin/AdminMediaLibrary")); 

// Expense Tracker (Both Roles)
const ExpOverviewPage = lazy(() => import("./Admin/ExpOverviewPage"));
const ExpAddTrans = lazy(() => import("./Admin/ExpAddTrans"));
const ExpViewTrans = lazy(() => import("./Admin/ExpViewTrans"));
const ExpCatManage = lazy(() => import("./Admin/ExpCatManage"));

// Todo List (Both Roles)
const TodoOverview = lazy(() => import("./Admin/TodoOverview"));
const TodoView = lazy(() => import("./Admin/TodoView"));
const TodoAdd = lazy(() => import("./Admin/TodoAdd"));
const TodoCatManage = lazy(() => import("./Admin/TodoCatManage"));

// Layout
const MainMasterLayout = lazy(() => import("./Admin/MainMasterLayout"));

// Admin-Exclusive Pages
const AdminAddUser = lazy(() => import("./Admin/AdminAddUser"));
const AdminAllUsers = lazy(() => import("./Admin/AdminAllUsers"));
const AdminViewUser = lazy(() => import("./Admin/AdminViewUser"));
const AdminContactLeads = lazy(() => import("./Admin/AdminContactLeads"));

const LoadingFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <div className="loading-spinner"></div>
  </div>
);

// Pages accessible to BOTH Admin and User
const commonRoutes = [
  { path: "dashboard", component: <Dashboard />, title: "Dashboard" },
  { path: "profile", component: <AdminProfile />, title: "Profile" },
  { path: "media-library", component: <AdminMediaLibrary />, title: "Media Library" },
  { path: "edit-profile", component: <AdmEditProfile />, title: "Edit Profile" },
  
  // Expense Tracking
  { path: "expense-tracker", component: <ExpOverviewPage />, title: "Expense Overview" },
  { path: "add-transaction", component: <ExpAddTrans />, title: "Add Transaction" },
  { path: "view-transactions", component: <ExpViewTrans />, title: "View Transactions" },
  { path: "expense-categories", component: <ExpCatManage />, title: "Expense Categories" },
  
  { path: "todo-overview", component: <TodoOverview />, title: "Task Overview" },
  { path: "add-todo", component: <TodoAdd />, title: "Add Task" },
  { path: "tasks-view", component: <TodoView />, title: "Manage Tasks" },
  { path: "todo-categories", component: <TodoCatManage />, title: "Task Categories" },
];

// Pages accessible ONLY to Admins
const adminOnlyRoutes = [
  { path: "add-user", component: <AdminAddUser />, title: "Add User" },
  { path: "view-user", component: <AdminViewUser />, title: "View User" },
  { path: "all-users", component: <AdminAllUsers />, title: "All Users" },
  { path: "contact-leads", component: <AdminContactLeads />, title: "Contact Leads" },
];

function App() {
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);

    const handleKeyDown = (e) => {
      if (e.key === "F12") e.preventDefault();
      if (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) e.preventDefault();
      if (e.ctrlKey && e.key === "U") e.preventDefault();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/expense-tracker" element={<ExpenseTracker />} />

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Automatically generates /admin-todo-overview & /user-todo-overview */}
          {['admin', 'user'].map(role => 
            commonRoutes.map((route) => (
              <Route 
                key={`${role}-${route.path}`} 
                path={`/${role}-${route.path}`} 
                element={<MainMasterLayout title={route.title}>{route.component}</MainMasterLayout>} 
              />
            ))
          )}
          
          {/* Admin Exclusive Routes */}
          {adminOnlyRoutes.map((route) => (
            <Route 
              key={`admin-${route.path}`} 
              path={`/admin-${route.path}`} 
              element={<MainMasterLayout title={route.title}>{route.component}</MainMasterLayout>} 
            />
          ))}

          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;